import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {boolean} */
  #isPrefetching = false;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);

    // Proactively prefetch on card mouseenter / pointerenter
    const card = this.closest('product-card, .pet-product-card, .card-product');
    if (card) {
      card.addEventListener('pointerenter', this.handlePrefetch, { once: true, passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Prefetches product page HTML in background for 0ms instant modal response
   */
  handlePrefetch = async () => {
    const currentUrl = this.productPageUrl;
    if (!currentUrl || this.#cachedContent.has(currentUrl) || this.#isPrefetching) return;

    this.#isPrefetching = true;
    try {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        let gridElement = html.querySelector('[data-product-grid-content]');
        if (!gridElement) {
          gridElement = html.querySelector('.pet-pdp-section, .pet-pdp-grid, product-component');
        }
        if (gridElement) {
          this.#cachedContent.set(currentUrl, gridElement.cloneNode(true));
        }
      }
    } catch (_) {
      // Ignore prefetch failures
    } finally {
      this.#isPrefetching = false;
    }
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        let gridElement = html.querySelector('[data-product-grid-content]');
        if (!gridElement) {
          gridElement = html.querySelector('.pet-pdp-section, .pet-pdp-grid, product-component');
        }
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media, .pet-pdp-media-col');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker and modal content.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Strip unneeded full PDP elements inside modal
    const elementsToRemove = productGrid.querySelectorAll(
      '.pet-pdp-zoom-modal, .pet-pdp-zoom-trigger, .pet-pdp-benefits-list, .pet-pdp-thumbs-swiper, .pet-swiper-btn, .pet-swiper-pagination, .pet-pdp-full-desc-container, .pet-pdp-tabs-wrap, .pet-pdp-trust-section, .pet-pdp-bottom-details, .pet-pdp-trust-badges-strip, .pet-pdp-trust-badges-wrapper, .pet-lightbox-modal',
    );
    elementsToRemove.forEach((el) => el.remove());

    // Only keep first active image in main slider if swiper isn't active
    const slides = productGrid.querySelectorAll('.pet-pdp-main-slide');
    if (slides.length > 1) {
      slides.forEach((slide, idx) => {
        if (idx > 0) slide.style.display = 'none';
      });
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    // Extract variants data for real-time variant switching
    let variants = [];
    try {
      const rawVariants = modalContent.getAttribute('data-variants') || productGrid.getAttribute('data-variants');
      if (rawVariants) {
        variants = JSON.parse(rawVariants);
      }
    } catch (_) {}

    // Interactive variant pill switching inside modal
    const modalPills = modalContent.querySelectorAll('.pet-pdp-variant-pill');
    modalPills.forEach((pill) => {
      pill.onclick = (e) => {
        e.preventDefault();
        const group = pill.closest('.pet-pdp-option-group');
        if (group) {
          group.querySelectorAll('.pet-pdp-variant-pill').forEach((p) => p.classList.remove('is-selected'));
          pill.classList.add('is-selected');

          const pos = pill.getAttribute('data-option-position');
          const val = pill.getAttribute('data-option-value');
          const labelVal = modalContent.querySelector('#PetOptionVal-' + pos);
          if (labelVal) labelVal.textContent = val;

          // Collect all selected options
          const selectedOptions = [];
          modalContent.querySelectorAll('.pet-pdp-option-group').forEach((g) => {
            const active = g.querySelector('.pet-pdp-variant-pill.is-selected');
            if (active) selectedOptions.push(active.getAttribute('data-option-value'));
          });

          if (variants.length > 0) {
            const matchedVariant = variants.find((v) => {
              return v.options.every((opt, idx) => opt === selectedOptions[idx]);
            });

            if (matchedVariant) {
              const variantIdInput = modalContent.querySelector('#PetPdpVariantId, input[name="id"]');
              const atcBtn = modalContent.querySelector('#PetPdpAtcBtn, button[name="add"]');
              const atcText = modalContent.querySelector('#PetPdpAtcText');
              const priceDisplay = modalContent.querySelector('#PetPdpPrice, .pet-pdp-current-price');
              const comparePriceDisplay = modalContent.querySelector('#PetPdpComparePrice, .pet-pdp-compare-price');
              const savingPill = modalContent.querySelector('#PetPdpSavingPill, .pet-pdp-saving-pill');
              const discountBadge = modalContent.querySelector('#PetPdpDiscountBadge, .pet-pdp-sale-badge');

              if (variantIdInput) variantIdInput.value = matchedVariant.id;

              const formattedPrice = '$' + (matchedVariant.price / 100).toFixed(2);

              // Update ATC Button
              if (matchedVariant.available) {
                if (atcBtn) atcBtn.disabled = false;
                if (atcText) atcText.textContent = 'Add To Cart • ' + formattedPrice;
              } else {
                if (atcBtn) atcBtn.disabled = true;
                if (atcText) atcText.textContent = 'Sold Out';
              }

              // Update Price Display
              if (priceDisplay) {
                priceDisplay.textContent = formattedPrice;
              }

              if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
                const formattedCompare = '$' + (matchedVariant.compare_at_price / 100).toFixed(2);
                const savePct = Math.round(
                  ((matchedVariant.compare_at_price - matchedVariant.price) / matchedVariant.compare_at_price) * 100,
                );
                const saveAmount = '$' + ((matchedVariant.compare_at_price - matchedVariant.price) / 100).toFixed(2);

                if (comparePriceDisplay) {
                  comparePriceDisplay.textContent = formattedCompare;
                  comparePriceDisplay.style.display = 'inline';
                }
                if (savingPill) {
                  savingPill.textContent = 'Save ' + savePct + '%';
                  savingPill.style.display = 'inline-flex';
                }
                if (discountBadge) {
                  discountBadge.textContent = '🔥 Save ' + savePct + '% (' + saveAmount + ')';
                  discountBadge.style.display = 'inline-flex';
                }
              } else {
                if (comparePriceDisplay) comparePriceDisplay.style.display = 'none';
                if (savingPill) savingPill.style.display = 'none';
                if (discountBadge) discountBadge.style.display = 'none';
              }

              // Update Image if variant has featured image
              if (matchedVariant.featured_image && matchedVariant.featured_image.src) {
                const mainImg = modalContent.querySelector('.pet-pdp-main-img');
                if (mainImg) {
                  mainImg.src = matchedVariant.featured_image.src;
                }
              }
            }
          }
        }
      };
    });

    // Interactive quantity steppers inside modal
    const qtyInput = modalContent.querySelector('.pet-pdp-qty-input, input[name="quantity"]');
    const minusBtn = modalContent.querySelector('#PetPdpQtyMinus, .pet-pdp-qty-btn:first-child');
    const plusBtn = modalContent.querySelector('#PetPdpQtyPlus, .pet-pdp-qty-btn:last-child');
    if (qtyInput && minusBtn && plusBtn) {
      minusBtn.onclick = (e) => {
        e.preventDefault();
        let val = parseInt(qtyInput.value, 10) || 1;
        if (val > 1) qtyInput.value = val - 1;
      };
      plusBtn.onclick = (e) => {
        e.preventDefault();
        let val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = val + 1;
      };
      qtyInput.onchange = () => {
        let val = parseInt(qtyInput.value, 10) || 1;
        if (val < 1) qtyInput.value = 1;
      };
    }

    // Modal Form Add-To-Cart AJAX Submission
    const pdpForm = modalContent.querySelector('#PetPdpForm, form[action*="/cart/add"]');
    if (pdpForm) {
      pdpForm.onsubmit = async (e) => {
        e.preventDefault();
        const atcBtn = modalContent.querySelector('#PetPdpAtcBtn, button[type="submit"]');
        const atcText = modalContent.querySelector('#PetPdpAtcText');
        const origText = atcText ? atcText.textContent : 'Add To Cart';

        if (atcBtn) atcBtn.disabled = true;
        if (atcText) atcText.textContent = 'Adding to cart...';

        try {
          const formData = new FormData(pdpForm);
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/javascript',
            },
          });

          if (response.ok) {
            if (atcText) atcText.textContent = '✓ Added to Cart!';
            // Dispatch cart line update event for Shopify theme cart drawer / count badge
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: { resource: 'cart' },
              }),
            );
            setTimeout(() => {
              this.#closeQuickAddModal();
              if (atcBtn) atcBtn.disabled = false;
              if (atcText) atcText.textContent = origText;
            }, 600);
          } else {
            throw new Error('Add to cart failed');
          }
        } catch (err) {
          if (atcText) atcText.textContent = origText;
          if (atcBtn) atcBtn.disabled = false;
          // Fallback to normal form submit if fetch fails
          pdpForm.submit();
        }
      };
    }

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
